import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NEVER, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { StringHelperService } from '../../../../services/string-helper.service';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { DocumentService } from '../../services/document.service';
import { categories, Company } from '../../models/models';
import { DragAndDropDirective } from '../../../../directives/drag-and-drop.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/input';
import { MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  imports: [
    DragAndDropDirective,
    MatTooltip,
    MatButton,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    AsyncPipe,
    MatIcon,
    MatProgressSpinner,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions
  ],
  selector: 'app-upload-file',
  standalone: true,
  styleUrls: ['./upload-file.component.scss'],
  templateUrl: './upload-file.component.html'
})
export class UploadFileComponent implements OnInit {
  documentName: string | null = null;
  file: File | null = null;
  formGroup = new FormGroup({
    category: new FormControl('', Validators.required),
    companyId: new FormControl('', Validators.required)
  });
  companies$: Observable<Company[]> | null = null;
  isUploading = false;
  readonly allowedFormats = '.pdf, .csv, .xls, .xlsx, .docx, .doc, .jpeg, .jpg, .png, .jfif, .gif, .txt, .ppt, .pptx';
  readonly categories = categories;

  private readonly fileMaxSize = 26214400;

  constructor(
    private documentService: DocumentService,
    private snackBar: MatSnackBar,
    private stringHelperService: StringHelperService,
    private dialogRef: MatDialogRef<UploadFileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { id: string | null; companyId: string | null }
  ) {}

  ngOnInit() {
    this.companies$ = this.documentService.getCompanies();
    if (this.data.companyId) {
      const customerControl = this.formGroup.get('companyId');
      customerControl?.setValue(this.data.companyId);
      customerControl?.disable();
    }
  }

  handleFileInput(files: FileList | EventTarget | null) {
    if (files) {
      if ((files as HTMLInputElement).files) {
        const inputFiles = (files as HTMLInputElement).files;
        this.file = inputFiles ? inputFiles[0] : null;
      } else {
        this.file = (files as FileList)[0];
      }
      this.documentName = this.file!.name;
    }
  }

  upload() {
    if (this.formGroup.invalid || !this.documentName) {
      this.formGroup.markAllAsTouched();
      this.snackBar.open('Please select all required fields', 'x');
      return;
    }

    if (this.stringHelperService.notAllowedSpecialChars(this.documentName)) {
      this.snackBar.open('File name cannot contain some special characters. Please rename the file.', 'x');
      return;
    }

    if (this.file && this.file.size > this.fileMaxSize) {
      this.snackBar.open('Max file size: 25MB', 'x');
      return;
    }

    this.uploadFile();
  }

  clear() {
    this.documentName = null;
    this.file = null;
    this.formGroup.reset();
  }

  private uploadFile() {
    if (this.file) {
      this.isUploading = true;
      this.documentService
        .upload(this.file, {
          ...this.formGroup.getRawValue(),
          folderId: this.data.id || ''
        })
        .pipe(
          tap((file: Document) => {
            this.dialogRef.close(file);
            this.snackBar.open('The file was uploaded successfully', 'x', { panelClass: 'snackbar-green' });
            this.isUploading = false;
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 409) {
              this.snackBar.open('This file already exists', 'x');
            } else {
              this.snackBar.open('There was an error. Please try again.', 'x');
            }
            this.isUploading = false;
            console.error(error);
            return NEVER;
          })
        )
        .subscribe();
    }
  }
}
